import './ClientProfileSection.scss';
import { StructuredListWrapper } from 'carbon-components-react';
import React, { ReactElement, useState } from 'react';
import { capitalize } from 'lodash';
import spacetime from 'spacetime';

import { HorizontalRule } from '../Theme/Theme';
import styled from 'styled-components';
import { UserIcon } from '../Icons/User';
import { OpenNow } from '../Icons/OpenNow';
import {
  ClientDetail,
  ClientDataInput,
  ExerciseAbility,
  Scalars,
} from '../../types/adminPortalApiSchema';
import { StyledBlueButton } from '../Button/Button';
import { UpdateUserArgs } from '../../pages/Client/Client';
import { ClientSaveSection } from './ClientSaveSection';
import { EditCycle } from '../Dialogs/EditCycle/EditCycle';
import { EditName } from '../Dialogs/EditName/EditName';

import {
  dateInDaysFromNow,
  dayDifferenceBetweenDates,
  EditDaysToCheckIn,
} from '../Dialogs/EditDaysToCheckIn/EditDaysToCheckIn';
import { CYCLE_LENGTH } from '../../constants';
import { orderCyclesByCreatedAtDate } from '../../utils/utils';
import { ClientProfileList, TopPaddedButton } from './ClientProfileList';
import { Edit16 } from '@carbon/icons-react';
import { WithBlueBullet } from '../Icons/BlueBullet';
import { EditInitialWeight } from '../Dialogs/EditInitialWeight/EditInitialWeight';
import {
  LifetimeUserSubscriptionDetails,
  AnnualUserSubscriptionDetails,
  NonAnnualUserSubscriptionDetails,
} from '../SubscriptionDetails/SubscriptionDetails';
import { EditEmail } from '../Dialogs/EditEmail/EditEmail';
import { ClientLinks } from './ClientLinks';

type Props = {
  user: ClientDetail;
  hasBeenEdited: boolean;
  updateUser: (args: UpdateUserArgs) => void;
  updatePendingUserChanges: (args: Partial<ClientDataInput>) => void;
  pendingUserChanges: ClientDataInput;
  isValid: boolean;
};

export const ClientProfileSection = ({
  user,
  hasBeenEdited,
  updateUser,
  updatePendingUserChanges,
  pendingUserChanges,
  isValid,
}: Props): ReactElement => {
  const latestCycle =
    (user &&
      user.cycles &&
      orderCyclesByCreatedAtDate(
        user.cycles.filter(({ cycleNumber }) => cycleNumber !== 0)
      )[0]) ||
    null;

  const calculateCheckInDateForCycleStart = (d: Date) =>
    new Date(new Date(d).setDate(new Date(d).getDate() + CYCLE_LENGTH));

  const latestCycleCheckInDate = calculateCheckInDateForCycleStart(
    new Date(latestCycle?.cycleCreatedAt)
  );

  const checkInCountdown = dayDifferenceBetweenDates(
    new Date(),
    latestCycleCheckInDate
  );

  const editCycleDefaultStateModal: {
    show: boolean;
    cycleId: string | null;
    ability: ExerciseAbility | null;
    cycleNumber: number | null;
  } = { show: false, cycleId: null, ability: null, cycleNumber: null };

  const editNameDefaultStateModal: {
    show: boolean;
    name: string | null;
    familyName: string | null;
  } = { show: false, name: null, familyName: null };

  const editEmailDefaultStateModal: {
    show: boolean;
    userId: string;
    email: string | null;
  } = { show: false, userId: user.id, email: null };

  const editDaysToCheckInDefaultStateModal: {
    show: boolean;
    daysToCheckIn: number | null;
  } = { show: false, daysToCheckIn: null };

  const editInitialWeightDefaultStateModal: {
    show: boolean;
    initialWeight: Scalars['Float'] | null;
  } = { show: false, initialWeight: null };

  const [isLoading, setLoading] = useState<boolean>(false);
  const [showEditCycle, setShowEditCycle] = useState(
    editCycleDefaultStateModal
  );
  const [showEditName, setShowEditName] = useState(editNameDefaultStateModal);
  const [showEditEmail, setShowEditEmail] = useState(
    editEmailDefaultStateModal
  );
  const [showDaysToCheckIn, setShowDaysToCheckIn] = useState(
    editDaysToCheckInDefaultStateModal
  );

  const [showInitialWeight, setShowInitialWeight] = useState(
    editInitialWeightDefaultStateModal
  );

  const editCycleModal = ({
    cycleId,
    ability,
    cycleNumber,
  }: {
    cycleId: string;
    ability: ExerciseAbility;
    cycleNumber: number;
  }): void => {
    setShowEditCycle({ show: true, cycleId, ability, cycleNumber });
  };

  const editDaysToCheckInModal = ({
    daysToCheckIn,
  }: {
    daysToCheckIn: number;
  }): void => {
    setShowDaysToCheckIn({ show: true, daysToCheckIn });
  };

  const editInitialWeightModal = ({
    initialWeight,
  }: {
    initialWeight: Scalars['Float'];
  }): void => {
    setShowInitialWeight({ show: true, initialWeight });
  };

  const editNameModal = ({
    name,
    familyName,
  }: {
    name: string;
    familyName?: string | null;
  }): void => {
    setShowEditName({ show: true, name, familyName: familyName ?? null });
  };

  const editEmailModel = ({
    userId,
    email,
  }: {
    userId: string;
    email: string;
  }): void => {
    setShowEditEmail({ show: true, userId, email });
  };

  const weightInKg = user.initialWeight?.measurement
    ? user.initialWeight?.measurement / 1000
    : 0;
  const dataToDisplay = {
    initialWeight: pendingUserChanges.initialWeight || weightInKg,
    name: pendingUserChanges.name || user.name,
    familyName: pendingUserChanges.familyName ?? user.familyName,
    ability:
      pendingUserChanges.currentAbilityAndCycle?.ability ||
      latestCycle?.ability,
    cycleNumber:
      pendingUserChanges.currentAbilityAndCycle?.cycleNumber ||
      latestCycle?.cycleNumber,
    cycleId: latestCycle?.cycleId,
    checkInCountdown:
      typeof pendingUserChanges.daysToCheckIn === 'number'
        ? pendingUserChanges.daysToCheckIn
        : checkInCountdown,
    checkInDate: dateInDaysFromNow(
      typeof pendingUserChanges.daysToCheckIn === 'number'
        ? pendingUserChanges.daysToCheckIn
        : checkInCountdown
    ),
  };

  const openNowCopy =
    dataToDisplay.checkInCountdown === 0
      ? 'Today'
      : `${-dataToDisplay.checkInCountdown} day${
          dataToDisplay.checkInCountdown === -1 ? '' : 's'
        } ago`;
  const nameCopy = `${dataToDisplay.name}${
    dataToDisplay.familyName ? ` ${dataToDisplay.familyName}` : ''
  }`;

  return (
    <>
      {showEditCycle?.cycleId &&
        showEditCycle?.ability &&
        showEditCycle?.cycleNumber && (
          <EditCycle
            headline={`Edit plan`}
            data={{
              cycleId: showEditCycle.cycleId,
              ability: showEditCycle.ability,
              cycleNumber: showEditCycle.cycleNumber,
            }}
            loading={isLoading}
            dismissButtonText="Cancel"
            confirmButtonText="OK"
            confirmButtonColor="blue"
            updateCycleCallback={async ({
              ensureCycleContentIsAvailable,
              ...dataToUpdate
            }): Promise<void> => {
              setLoading(true);
              // Check there is some workout content for the ability / cycle number
              const cycleIsValid = await ensureCycleContentIsAvailable();
              setLoading(false);

              if (cycleIsValid) {
                const hasChanged =
                  latestCycle.ability !== dataToUpdate.ability ||
                  latestCycle.cycleNumber !== dataToUpdate.cycleNumber;
                updatePendingUserChanges({
                  currentAbilityAndCycle: hasChanged ? dataToUpdate : null,
                });
                setShowEditCycle(editCycleDefaultStateModal);
              }
            }}
            dismissButtonCallback={(): void => {
              setShowEditCycle(editCycleDefaultStateModal);
            }}
          />
        )}
      {typeof showDaysToCheckIn?.daysToCheckIn === 'number' && (
        <EditDaysToCheckIn
          headline={`Edit next check-in`}
          data={{
            daysToCheckIn: dataToDisplay.checkInCountdown,
          }}
          loading={isLoading}
          dismissButtonText="Cancel"
          confirmButtonText="OK"
          confirmButtonColor="blue"
          updateCheckInCallback={async ({ daysToCheckIn }): Promise<void> => {
            const hasChanged = checkInCountdown !== daysToCheckIn;
            updatePendingUserChanges({
              daysToCheckIn: hasChanged ? daysToCheckIn : null,
            });
            setShowDaysToCheckIn(editDaysToCheckInDefaultStateModal);
          }}
          dismissButtonCallback={(): void => {
            setShowDaysToCheckIn(editDaysToCheckInDefaultStateModal);
          }}
        />
      )}

      {typeof showInitialWeight?.initialWeight === 'number' && (
        <EditInitialWeight
          headline={`Edit initial weight.`}
          data={{
            initialWeight: dataToDisplay.initialWeight,
          }}
          loading={isLoading}
          dismissButtonText="Cancel"
          confirmButtonText="OK"
          confirmButtonColor="blue"
          updateInitialWeightCallback={async ({
            initialWeight,
          }): Promise<void> => {
            const hasChanged = weightInKg !== initialWeight;
            updatePendingUserChanges({
              initialWeight: hasChanged ? initialWeight : null,
            });
            setShowInitialWeight(editInitialWeightDefaultStateModal);
          }}
          dismissButtonCallback={(): void => {
            setShowInitialWeight(editInitialWeightDefaultStateModal);
          }}
        />
      )}
      {typeof showEditName?.name === 'string' && (
        <EditName
          headline={`Edit the user's name`}
          data={{
            name: dataToDisplay.name,
            familyName: dataToDisplay.familyName,
          }}
          loading={isLoading}
          dismissButtonText="Cancel"
          confirmButtonText="OK"
          confirmButtonColor="blue"
          updateNameCallback={({ name, familyName }) => {
            const nameHasChanged = name !== user.name;
            const familyNameHasChanged = familyName !== user.familyName;
            updatePendingUserChanges({
              name: nameHasChanged ? name : null,
              familyName: familyNameHasChanged ? familyName : null,
            });
            setShowEditName(editNameDefaultStateModal);
          }}
          dismissButtonCallback={(): void => {
            setShowEditName(editNameDefaultStateModal);
          }}
        />
      )}
      {typeof showEditEmail?.email === 'string' && (
        <EditEmail
          headline={`Edit the user's email`}
          data={{
            userId: showEditEmail.userId,
            email: showEditEmail.email,
          }}
          dismissButtonText="Cancel"
          confirmButtonText="OK"
          confirmButtonColor="blue"
          updateEmailCallback={async (email) => {
            updateUser({ update: { email } });
          }}
          dismissButtonCallback={(): void => {
            setShowEditEmail(editEmailDefaultStateModal);
          }}
        />
      )}

      <Wrapper>
        <StyledUserIconWrapper>
          <UserIcon />
        </StyledUserIconWrapper>
        <HeadingWrapper>
          <SubHeadingWrapper>
            <Title>
              {pendingUserChanges.name != null ||
              pendingUserChanges.familyName != null ? (
                <WithBlueBullet copy={nameCopy} leftWidth={70} />
              ) : (
                nameCopy
              )}
            </Title>
            {dataToDisplay.ability &&
              dataToDisplay.cycleId &&
              dataToDisplay.cycleNumber && (
                <TopPaddedButton
                  kind="ghost"
                  data-testid="edit-name-icon"
                  iconDescription="icon"
                  renderIcon={Edit16}
                  onClick={(): void => {
                    editNameModal({
                      name: dataToDisplay.name,
                      familyName: dataToDisplay.familyName,
                    });
                  }}
                />
              )}
          </SubHeadingWrapper>
          <UserInfo>
            <Light>{`Email:`}</Light>
            <span>{user.email}</span>
            <TopPaddedButton
              kind="ghost"
              data-testid="edit-name-icon"
              iconDescription="icon"
              renderIcon={Edit16}
              onClick={(): void => {
                editEmailModel({ userId: user.id, email: user.email });
              }}
            />
          </UserInfo>
          <UserInfo>
            <Light>{`User ID:`}</Light>
            <span>{user.id}</span>
          </UserInfo>

          <ClientLinks user={user} />

          <SubscriptionHeading>Subscription Details</SubscriptionHeading>
          <LifetimeUserSubscriptionDetails
            subscription={user.currentSubscription}
          />
          <AnnualUserSubscriptionDetails
            subscription={user.currentSubscription}
          />
          <NonAnnualUserSubscriptionDetails
            subscription={user.currentSubscription}
          />
          <ClientSaveSection
            user={user}
            hasBeenEdited={hasBeenEdited}
            updateUser={updateUser}
            isValid={isValid}
            pendingUserChanges={pendingUserChanges}
            updatePendingUserChanges={updatePendingUserChanges}
            genericError="Failed to update client profile"
          />
        </HeadingWrapper>

        <ProfileWrapper>
          <ProfileContentWrapper>
            <LeftStructuredListWrapper
              style={{ width: '100%' }}
              ariaLabel="Structured list"
            >
              <ClientProfileList
                header={'Client Details'}
                editCycleModalCallback={editCycleModal}
                editDaysToCheckInCallback={editDaysToCheckInModal}
                editInitialWeightCallback={editInitialWeightModal}
                listItems={[
                  {
                    hide: !user.sex,
                    text: 'Sex',
                    value: capitalize(user.sex as string),
                  },
                  {
                    hide: !user.dob,
                    text: 'DOB',
                    value: spacetime(new Date(user.dob))
                      .format('{date-pad}/{iso-month}/{year}')
                      .toString(),
                  },
                  {
                    hide: !latestCycle,
                    editable: true,
                    text: 'Check-in day',
                    value:
                      dataToDisplay.checkInCountdown < 1 ? (
                        <OpenNow copy={openNowCopy} />
                      ) : (
                        `${dataToDisplay.checkInCountdown} day${
                          dataToDisplay.checkInCountdown === 1 ? '' : 's'
                        }  |  ${spacetime(dataToDisplay.checkInDate)
                          .format('{date-pad}/{iso-month}/{year}')
                          .toString()}`
                      ),
                    daysToCheckIn: checkInCountdown,
                    isDirty: pendingUserChanges.daysToCheckIn != null,
                  },
                  {
                    hide: !user.createdAt,
                    text: 'Account creation',
                    value: spacetime(user.createdAt)
                      .format('{date-pad}/{iso-month}/{year}')
                      .toString(),
                  },
                  {
                    hide: !latestCycle,
                    editable: true,
                    text: 'Initial Weight (kg)',
                    value: dataToDisplay.initialWeight,
                    initialWeight: user.initialWeight?.measurement,
                    isDirty: pendingUserChanges.initialWeight != null,
                  },
                  {
                    hide: !user?.height?.measurement,
                    text: 'Height (cm)',
                    value: user?.height?.measurement
                      ? user?.height?.measurement / 10
                      : 0,
                  },
                ]}
              ></ClientProfileList>
            </LeftStructuredListWrapper>
          </ProfileContentWrapper>
        </ProfileWrapper>
      </Wrapper>
      <HorizontalRule />
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: bold;
  margin-bottom: 20px;
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const ProfileContentWrapper = styled.div`
  display: flex;
`;

const LeftStructuredListWrapper = styled((props) => (
  <StructuredListWrapper {...props} />
))``;

export const SubscriptionHeading = styled.h2`
  font-weight: bold;
  font-size: ${(props): string => props.theme.l};
  margin: 20px 0px;
`;

export const UserInfo = styled.div`
  font-size: 1rem;
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SubHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledUserIconWrapper = styled.div`
  margin-right: 10px;
  margin-top: 2px;
`;

const Light = styled.span`
  margin-right: 5px;
  color: ${(props): string => props.theme.bodyCopyLight};
`;

export const StyledSaveChangesButton = styled((props) => (
  <StyledBlueButton {...props} />
))`
  margin-top: 16px;
  max-width: 178px;
`;
